import React, { Component, useRef } from 'react';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import PageViewTracker from './components/behavior/PageViewTracker';
import { ScrollToTopManager } from './components/behavior/ScrollToTopManager';
import ContentContainer from './containers/ContentContainer';
import MainLayout from './layout/MainLayout';
import BookmarkProvider from './provider/BookmarkProvider';
import BreakpointProvider from './provider/BreakpointProvider';
import LanguageProvider from './provider/LanguageProvider';
import ModalProvider from './provider/ModalProvider';
import NearestVehicleCenterProvider from './provider/NearestVehicleCenterProvider';
import SearchProvider from './provider/SearchProvider';
import TrackingProvider from './provider/TrackingProvider';
import Typo3Provider from './provider/Typo3Provider';
import { UrlLanguageProvider } from './provider/UrlLanguageProvider';
import UsercentricsProvider from "./provider/UsercentricsProvider";
import { getLanguageFromPathname } from './utils/getLanguageFromPathname';
import { FootnoteProvider } from './provider/FootnoteProvider/FootnoteProvider';

interface AppProps {
  languages: string[];
}

function AppInternal ({pathname}: {pathname: string}) {
  const initialLanguageUid = useRef(getLanguageFromPathname(pathname));
  const currentLanguageUid = getLanguageFromPathname(pathname);

  if (currentLanguageUid !== initialLanguageUid.current) {
    // Reload when language uid (retrieved from path) changed
    window.location.reload();
    return null;
  }

  return (
    <Typo3Provider>
      <UsercentricsProvider>
        <TrackingProvider>
          <ScrollToTopManager />
          <PageViewTracker />
          <LanguageProvider>
            <BookmarkProvider>
              <SearchProvider>
                <NearestVehicleCenterProvider>
                  <ModalProvider>
                    <FootnoteProvider>
                      <MainLayout>
                        <ContentContainer />
                      </MainLayout>
                    </FootnoteProvider>
                  </ModalProvider>
                </NearestVehicleCenterProvider>
              </SearchProvider>
            </BookmarkProvider>
          </LanguageProvider>
        </TrackingProvider>
      </UsercentricsProvider>
    </Typo3Provider>
  );
}

// #38319 removing certain meta information
const selectorsToBeRemoved = [
  'link[rel=canonical]',
  'link[rel=alternate]',
  'meta[name=description]',
  'meta[name=keywords]',
  'meta[property="og:url"]',
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:image"]',
  'meta[property="og:image:url"]',
  'meta[property="og:image:width"]',
  'meta[property="og:image:height"]',
  'meta[name="twitter:description"]',
];

function redirectExternal(props: RouteComponentProps<any>): React.ReactNode {
    const {pathname, search, hash} = props.location;
    window.location.href = `${pathname}${search}${hash}`;
    return null;
}

class App extends Component<AppProps> {
  public componentDidMount() {

    // #38319 removing certain meta information
    selectorsToBeRemoved.forEach(selector => {
      document.querySelectorAll(selector).forEach((node) => {
        node.parentNode && node.parentNode.removeChild(node);
      });
    });
    if (process.env.NODE_ENV === 'production' && window.location.search.includes('debug=true')) {
      document.cookie = 'debug=true; path=/; expires=session';
    }
  }

  public render() {
    return (
      <UrlLanguageProvider>
        <BreakpointProvider>
          <Switch>
            {/* TODO: doc! */}
            <Route exact path="/" render={() => {
              // Redirect for development; Language redirects are handled in .htaccess #50156
              window.location.href = '/de/';
              return null;
            }} />
            <Route
              path="/fileadmin/user_upload/"
              render={redirectExternal}
            />
            <Route
              render={({ location }) => {
                const { pathname, search, hash } = location;
                // check if pathname lacks trailing slash
                if (pathname.match(/.*[^/]$/g)) {
                  // no <Redirect /> because we want to circumvent the frontend
                  window.location.href = `${pathname}/${search}${hash}`;
                  return null;
                }

                return <AppInternal pathname={pathname} />;
              }}
            />
          </Switch>
        </BreakpointProvider>
      </UrlLanguageProvider>
    );
  }
}

export default App;
